import { useEffect, useState } from "react";
import { ShieldCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function MfaSetup({ challenge, onDone }) {
  const [qr, setQr] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [pending, setPending] = useState(null); // holds session {token,user} until codes acknowledged
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/auth/mfa/setup/start", { challenge });
        setQr(data.qr_png_data_uri);
        setManualKey(data.manual_key);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not start setup");
      }
    })();
  }, [challenge]);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup/verify", { challenge, code });
      setRecovery(data.recovery_codes || []);
      setPending({ token: data.token, user: data.user });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText((recovery || []).join("\n")).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  if (recovery) {
    return (
      <div className="glass glass-cyan rounded-2xl p-7" data-testid="mfa-recovery-panel">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          <h1 className="font-head font-bold text-lg">Save your recovery codes</h1>
        </div>
        <p className="text-xs text-muted-foreground mb-4 font-mono">
          Store these somewhere safe. Each code works once if you lose your authenticator. They won't be shown again.
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-lg border border-white/10 bg-slate-900/60 p-4 font-mono text-sm" data-testid="recovery-codes">
          {recovery.map((c) => <span key={c} className="text-cyan-300">{c}</span>)}
        </div>
        <button onClick={copyCodes} data-testid="copy-recovery-btn"
                className="mt-3 text-xs font-mono text-muted-foreground hover:text-foreground flex items-center gap-1.5">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy all codes"}
        </button>
        <Button onClick={() => onDone(pending)} data-testid="mfa-setup-continue"
                className="w-full mt-5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
          I've saved them — continue
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={verify} className="glass glass-cyan rounded-2xl p-7" data-testid="mfa-setup-form">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-cyan-400" />
        <h1 className="font-head font-bold text-lg">Set up two-factor</h1>
      </div>
      <p className="text-xs text-muted-foreground mb-4 font-mono">
        Two-factor is required. Scan this QR with Google Authenticator, Authy or 1Password, then enter the 6-digit code.
      </p>

      <div className="flex justify-center mb-4">
        {qr ? <img src={qr} alt="Scan with your authenticator app" data-testid="mfa-qr" className="w-44 h-44 rounded-lg bg-white p-2" />
            : <div className="w-44 h-44 rounded-lg bg-slate-900/60 animate-pulse" />}
      </div>

      {manualKey && (
        <p className="text-[11px] text-muted-foreground font-mono text-center mb-4 break-all" data-testid="mfa-manual-key">
          Can't scan? Enter this key: <span className="text-cyan-300">{manualKey}</span>
        </p>
      )}

      <div className="space-y-1">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Enter the 6-digit code</Label>
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
               inputMode="numeric" placeholder="123456" data-testid="mfa-setup-code-input"
               className="bg-slate-900/60 border-white/10 tracking-[0.4em] text-center font-mono text-lg" />
      </div>

      <Button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-setup-verify"
              className="w-full mt-5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
        {busy ? "Verifying…" : "Verify & enable"}
      </Button>
    </form>
  );
}
